// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A specialized timeline event component for asset transit activities.
 * This module provides a visual representation of asset movements
 * and transit states, with support for both planned and actual
 * transit events. It handles different duration formats and
 * provides detailed tooltips with event timing information.
 *
 * Key features:
 * - Transit state display
 * - Duration formatting
 * - Planned vs actual states
 * - Detailed tooltips
 * - Edit capabilities
 */
import { Asset, Event, EventGroup } from '@/asset/api/types';
import { EventGroupDialog } from '@/asset/components/event-group-dialog';
import { AssetTimelineEvent } from '@/asset/components/timeline-event';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Interval } from 'luxon';

/**
 * Props for configuring the AssetTransitEvent component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset being transported.
   * Used for displaying and editing transit details.
   */
  asset: Asset;

  /**
   * The event group containing transit information.
   * Includes timing and location details for the move.
   */
  eventGroup: EventGroup;
}

/**
 * A specialized timeline event for asset transit activities.
 * Displays an asset's movement between locations with
 * appropriate labels and timing information.
 *
 * Features:
 * - Smart label selection based on duration
 * - Planned vs actual state differentiation
 * - Detailed timing tooltips
 * - Edit capability through dialog
 * - Fallback display for invalid intervals
 *
 * Label Logic:
 * - "Delivered" for moves where any deliver event has occurred
 * - "In transit" for moves where ship event occurred but no delivery
 * - "Promised" for planned moves with only promise dates
 * - "Planned" for planned moves with only expected dates
 * - "Scheduled" for planned moves with mixed promise/expected dates
 * - "Move" for single-day completed events (fallback)
 *
 * Usage example:
 * ```tsx
 * function AssetTimeline({ asset, eventGroup }: Props) {
 *   return (
 *     <AssetTransitEvent
 *       asset={asset}
 *       eventGroup={eventGroup}
 *     />
 *   );
 * }
 * ```
 */
export function AssetTransitEvent(props: Props) {
  const dialog = useDisclosure();

  if (props.eventGroup.minimumDate && props.eventGroup.maximumDate) {
    const interval = Interval.fromDateTimes(
      props.eventGroup.minimumDate,
      props.eventGroup.maximumDate
    );

    if (interval.isValid) {
      const isDeliverEvent = (event: Event) =>
        event.type.name.toLowerCase() === 'deliver';

      const isShipEvent = (event: Event) =>
        event.type.name.toLowerCase() === 'ship';

      const hasDelivered = props.eventGroup.events.some(
        (event) => isDeliverEvent(event) && event.occurredAt !== null
      );

      const hasShipped = props.eventGroup.events.some(
        (event) => isShipEvent(event) && event.occurredAt !== null
      );

      // Determine if all events are planned (no actual dates)
      const isPlanned = props.eventGroup.events.every(
        (event) => event.occurredAt === null
      );

      let label: string;

      if (hasDelivered) {
        label = 'Delivered';
      } else if (hasShipped) {
        label = 'In transit';
      } else if (isPlanned) {
        const hasPromiseDates = props.eventGroup.events.some(
          (event) => event.promiseDate !== null
        );
        const hasExpectedDates = props.eventGroup.events.some(
          (event) => event.expectedAt !== null
        );

        if (hasPromiseDates && hasExpectedDates) {
          label = 'Scheduled';
        } else if (hasPromiseDates) {
          label = 'Promised';
        } else if (hasExpectedDates) {
          label = 'Planned';
        } else {
          // Fallback for events with no dates
          label = interval.start.equals(interval.end) ? 'Move' : 'In transit';
        }
      } else {
        label = interval.start.equals(interval.end) ? 'Move' : 'In transit';
      }

      // Format description based on duration
      const dayLength = interval.length('days');
      const description = interval.toLocaleString({
        dateStyle: 'medium',
        timeStyle: dayLength > 1 ? undefined : 'short',
      });

      // Create detailed tooltip with event timing
      const tooltip = (
        <ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
          {props.eventGroup.events.map((event) => {
            const dateTime = event.occurredAt ?? event.expectedAt;

            if (dateTime === null) {
              return null;
            }

            return (
              <li style={{ whiteSpace: 'nowrap' }} key={event.id}>
                {event.type.name}:{' '}
                {dateTime.toFormat('MMM d, yyyy @ hh:mm:ss a (z)')}
              </li>
            );
          })}
        </ul>
      );

      return (
        <>
          <AssetTimelineEvent
            type="activity"
            label={label}
            tooltip={tooltip}
            description={description}
            onEdit={dialog.open}
          />
          <EventGroupDialog
            asset={props.asset}
            isOpen={dialog.isOpen}
            onClose={dialog.close}
            eventGroup={props.eventGroup}
            configuration={props.eventGroup.configuration}
          />
        </>
      );
    }
  }

  // Fallback for moves with no dates — always editable
  return (
    <>
      <AssetTimelineEvent
        type="activity"
        label="Open move"
        onEdit={dialog.open}
      />
      <EventGroupDialog
        asset={props.asset}
        isOpen={dialog.isOpen}
        onClose={dialog.close}
        eventGroup={props.eventGroup}
        configuration={props.eventGroup.configuration}
      />
    </>
  );
}
